import { User } from "@prisma/client";
import { Application } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import Badge, { BadgeType } from "immigram-ui-cmon/src/components/Badge/Badge";
import Button, {
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import { Icon24Trash } from "immigram-ui-cmon/src/components/Icons";
import { showNotification } from "immigram-ui-cmon/src/components/Notification/Notification";
import Toggle from "immigram-ui-cmon/src/components/Toggle/Toggle";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

import { isAdmin } from "@/lib/auth/rbac";
import { ExperienceType } from "@/lib/experienceType";
import useBooleanState from "@/lib/hook/useBooleanState";
import useTransparentState from "@/lib/hook/useTransparentState";

import { ImmigramFormConnected } from "@/components/ImmigramForm/ImmigramFormConnected";
import FormModal from "@/components/Modal/FormModal";
import { showConfirmModal } from "@/components/Modal/ModalController";
import { PROJECT_EXP_TYPE_TO_NAMESPACE } from "@/containers/ProfJourney";

import { useApplicationForm } from "@/core/client/application/applicationForm/useApplicationForm";
import { useApplicationFormFieldMutation } from "@/core/client/application/applicationForm/useApplicationFormFieldMutation";

interface ProfJourneyProjectModalProps {
  application: Application;
  ownUser: User;
  experienceType?: ExperienceType;
  formInstanceId?: string;
  formParentId?: string;
  open?: boolean;
  position?: string;
  projectName?: string;
  editable?: boolean;
  isValid?: boolean;
  showErrors?: boolean;
  externalFormValues?: Record<string, string>;
  onClose: () => void;
  onSave: () => void;
  onFieldChange?: (itemConfig: any, value: string) => void;
  onProjectCreate?: (formInstanceId: string) => void;
  onProjectDelete?: ({
    projectName,
    formInstanceId,
  }: {
    projectName?: string;
    formInstanceId: string;
  }) => void;
}

export function ProfJourneyProjectModal({
  open,
  onClose,
  onSave,
  onFieldChange,
  onProjectCreate,
  onProjectDelete,
  showErrors,
  isValid = true,
  editable,
  application,
  formInstanceId,
  formParentId,
  ownUser,
  experienceType,
  position,
  projectName,
  externalFormValues,
}: ProfJourneyProjectModalProps) {
  const { t } = useTranslation();

  const [instanceId, setInstanceId] = useTransparentState<string | undefined>(
    formInstanceId
  );

  const { mutateAsync: applicationFormFieldUpdate } =
    useApplicationFormFieldMutation();

  const handleFormInstanceCreate = useCallback(
    (formInstanceId: string) => {
      setInstanceId(formInstanceId);
      onProjectCreate?.(formInstanceId);
    },
    [onProjectCreate, setInstanceId]
  );

  const formIsLoadingState = useBooleanState(false);

  const [isFormValid, setIsFormValid] = useTransparentState<
    boolean | undefined
  >(isValid);
  const [showFormErrors, setShowFormErrors] = useTransparentState<
    boolean | undefined
  >(showErrors);

  useEffect(() => {
    setShowFormErrors(false);
  }, [open, setShowFormErrors]);

  const handleSaveAndCloseClick = useCallback(() => {
    if (isFormValid) {
      onSave();
    } else {
      setShowFormErrors(true);
    }
  }, [isFormValid, onSave, setShowFormErrors]);

  const handleProjectDelete = useCallback(() => {
    showConfirmModal({
      heading: t("application:professionalJourneyCompany.deleteHeading", {
        name:
          projectName ||
          t(
            "application:profJourney.profJourneyProjectModal.heading.name.project"
          ),
      }),
      text: t("application:professionalJourneyCompany.deleteText"),
      confirmButtonText: t("application:professionalJourneyCompany.delete"),
      onConfirm: async () => {
        try {
          if (!instanceId) {
            throw new Error("No project to delete");
          }
          onProjectDelete?.({
            projectName: projectName,
            formInstanceId: instanceId,
          });
          onClose();
        } catch (e) {
          showNotification({
            variant: "alert",
            title: t(
              "application:professionalJourneyCompany.notification.failedToDelete"
            ),
          });
        }
      },
    });
  }, [onProjectDelete, instanceId, projectName, onClose, t]);

  const formNamespace =
    experienceType && PROJECT_EXP_TYPE_TO_NAMESPACE[experienceType];

  const heading = projectName
    ? projectName
    : t(
        "application:profJourney.profJourneyProjectModal.heading.describeProject"
      );

  const { data: formData } = useApplicationForm(
    application.id,
    formInstanceId,
    {
      keepPreviousData: false,
      enabled: !!formInstanceId,
    }
  );

  const initialState = useMemo(() => {
    return formData?.fields.some(
      (obj) =>
        obj.formId === instanceId &&
        obj.name === "withoutDetails" &&
        obj.value === "yes"
    );
  }, [instanceId, formData?.fields]);

  const [withoutDetails, setWithoutDetails] = useState(initialState);

  useEffect(() => {
    setWithoutDetails(initialState);
  }, [instanceId, initialState]);

  const queryClient = useQueryClient();

  const handleFieldChange = useCallback(async () => {
    if (instanceId) {
      setWithoutDetails(!withoutDetails);

      await applicationFormFieldUpdate({
        applicationId: application.id,
        formInstanceId: instanceId,
        name: "withoutDetails",
        type: "toggle",
        value: withoutDetails ? "no" : "yes",
      } as any);

      queryClient.invalidateQueries([
        "application-form-list-query",
        application.id,
      ]);
    }
  }, [
    withoutDetails,
    setWithoutDetails,
    instanceId,
    application.id,
    applicationFormFieldUpdate,
    queryClient,
  ]);

  return (
    <>
      <FormModal
        applicationSectionStatus={1}
        heading={
          <div className="flex items-center gap-3">
            {heading}
            {position && (
              <Badge size="150" type={BadgeType.Neutral}>
                {position}
              </Badge>
            )}
          </div>
        }
        isSelfService={false}
        open={open || false}
        fullScreen={true}
        innerClassName="gap-y-0"
        isAdmin={isAdmin(ownUser)}
        onCollapse={onClose}
        hasCompletedButton={true}
        scrollY={!formIsLoadingState.value}
        actionButton={
          formIsLoadingState.value ? (
            <></>
          ) : (
            <div className="flex gap-x-3">
              {editable ? (
                <>
                  <Button
                    className="inline-flex"
                    type={ButtonType.Primary}
                    disabled={showFormErrors && !isFormValid}
                    onClick={handleSaveAndCloseClick}
                  >
                    {t(
                      "application:profJourney.profJourneyProjectModal.button.saveAndClose"
                    )}
                  </Button>
                  <Button
                    className="inline-flex"
                    type={ButtonType.Secondary}
                    onClick={handleProjectDelete}
                    icon={<Icon24Trash />}
                  >
                    {t(
                      "application:profJourney.profJourneyProjectModal.button.deleteProject"
                    )}
                  </Button>
                </>
              ) : null}
              {isAdmin(ownUser) && (
                <Toggle
                  label={
                    t("application:employment.field.company.without_details") ??
                    ""
                  }
                  isEnabled={withoutDetails}
                  onChange={handleFieldChange}
                />
              )}
            </div>
          )
        }
      >
        <div
          className={clsxm(
            "w-full flex-grow items-start pb-[70px] sm:grid sm:grid-cols-6 lg:mx-auto lg:grid-cols-12 lg:gap-8"
          )}
        >
          <div className="col-span-7">
            <ImmigramFormConnected
              externalFormValues={externalFormValues}
              showValidationErrors={showFormErrors}
              onIsValidChange={setIsFormValid}
              applicationId={application.id}
              formInstanceId={formInstanceId}
              formNamespace={formNamespace}
              formParentId={formParentId}
              onFormInstanceCreate={handleFormInstanceCreate}
              onFieldChange={onFieldChange}
              onLoadingStart={formIsLoadingState.setTrue}
              onLoadingEnd={formIsLoadingState.setFalse}
              editable={editable}
              isO1Visa={application.visaTypeId === 2}
            />
          </div>
        </div>
      </FormModal>
    </>
  );
}
